"use client";

import React from "react";
import { ClipboardCheck, PenTool, Wrench, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Technical Scoping",
  description: "We analyze your infrastructure requirements, from G652D fiber attenuation specs to AI data center bandwidth needs, ensuring a high-precision foundation.",
  icon: ClipboardCheck
}, {
  title: "Precision Engineering",
  description: "Our engineers design bespoke architectures for optical networks or drone systems, focusing on low-latency connectivity and structural reliability.",
  icon: PenTool
}, {
  title: "Rigorous Validation",
  description: "Every component undergoes stress testing—from flight controller stability to fiber bending resistance—to meet international quality standards.",
  icon: Wrench
}, {
  title: "System Deployment",
  description: "We execute final integration and activation, providing comprehensive technical documentation and global support for seamless operational handover.",
  icon: Zap
}];
function TimelineStep({
  step,
  index,
  total
}: {
  step: typeof steps[0];
  index: number;
  total: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="91183" ref={ref} data-index={index} className="relative flex flex-col md:flex-row items-start md:items-center w-full mb-24 last:mb-0 group">
      {}
      {index !== total - 1 && <div className={cn("absolute left-[23px] md:left-1/2 top-8 bottom-[-6rem] w-[2px] -translate-x-1/2 transition-all duration-1000 ease-in-out", isVisible ? "bg-primary shadow-[0_0_15px_hsl(var(--primary))]" : "bg-primary/10")} />}

      {}
      <div className={cn("absolute left-[23px] md:left-1/2 top-0 w-8 h-8 rounded-full -translate-x-1/2 border-4 border-background transition-all duration-700 ease-out z-20 flex items-center justify-center", isVisible ? "bg-primary shadow-[0_0_25px_hsl(var(--primary))] scale-110" : "bg-muted/30 scale-100")}>
        <div className="w-2 h-2 bg-background rounded-full" />
      </div>

      {}
      <div className={cn("w-full md:w-1/2 pl-16 md:pl-0 transition-all duration-1000 ease-out", isEven ? "md:pr-16" : "md:pl-16 md:ml-auto", isVisible ? "opacity-100 translate-x-0 translate-y-0" : cn("opacity-0 translate-y-8", isEven ? "md:-translate-x-16" : "md:translate-x-16"))}>
        <Card className="relative overflow-hidden bg-card/40 backdrop-blur-2xl border-border/30 shadow-2xl hover:border-primary/50 transition-colors duration-500">
          {}
          <div className="absolute -top-8 -right-6 text-[12rem] font-black text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.05)] opacity-20 select-none pointer-events-none z-0 transition-transform duration-700 group-hover:scale-105 group-hover:-translate-y-2 font-mono">
            0{index + 1}
          </div>

          <CardHeader className="relative z-10 pb-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 border border-primary/30 shadow-[inset_0_0_20px_rgba(0,255,255,0.1)]">
              <step.icon className="w-7 h-7 text-primary drop-shadow-[0_0_10px_hsl(var(--primary))]" />
            </div>
            <CardTitle className="text-2xl md:text-3xl text-foreground font-bold tracking-tighter">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <CardDescription className="text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
              {step.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function ServiceProcess() {
  return <section id="integration-process" className="dark bg-background text-foreground py-24 md:py-32 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[160px] pointer-events-none opacity-50" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground tracking-tighter uppercase">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/40 drop-shadow-md">Integration Process</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans">
            A systematic engineering approach to deploying next-generation optical networks and advanced drone systems for global enterprises.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {steps.map((step, index) => <TimelineStep key={index} step={step} index={index} total={steps.length} />)}
        </div>
      </div>
    </section>;
}